import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';

import {
  smallUpsCloneForCreate,
  smallUpsCreateDraft,
  smallUpsEnsureNameForCreate,
} from './subsections/smallUpsEngineBindings';
import { SmallUpsEngineEditFormBody } from './subsections/SmallUpsEngineEditFormBody';
import {
  isSmallUpsPersistedProps,
  type SmallUpsProps,
  type SmallUpsPersistedProps,
  type SmallUpsInitialProps,
} from './useSmallUps';
import { Eye, EyeOff } from '../../../components/icons';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { buildLegacySmallUpsFromRequestFields } from '../../../components/smallups/smallUpsListUtils';
import { StepShell } from '../../../components/StepShell';
import { useSmallUpsConfigurations } from '../../../hooks/queries/useSmallUpsConfigurations';
import {
  descriptorInstanceToSmallUps,
  smallUpsToDescriptorInstance,
} from '../../../services/descriptorEditSessionFlush';
import {
  DescriptorEngineSection,
  type DescriptorInstance,
} from '../../sections/descriptorEngineAdapter';
import { SMALL_UPS_DESCRIPTOR } from '../../sections/smallUps.descriptor';

import type { SmallUpsConfigurationsSaveHandle } from './subsections/smallUpsConfigurationsSaveHandle';
import type { RepeatableInstanceEditFormRenderProps } from '../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../../components/repeatableSections/repeatableInstanceSaveHandle';
import type { SmallUpsConfiguration } from '../../../types/smallUps';

const SmallUpsInitialLayout: React.FC<SmallUpsInitialProps> = ({
  formData,
  updateFormData,
  requestId,
  disabled = false,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  registerSmallUpsSaveHandle,
  onSmallUpsEditorActiveChange,
}) => {
  const stage = String(formData.stage ?? formData.sysRequestStage ?? 'DRAFT');

  // #997 Story 6.4 — the create/draft layout runs on the descriptor engine
  // (mirror RackPower 6.3). The engine owns the list/edit-session/save/flush/
  // attachment lifecycle (6.1 per-unit scoping + 6.2 temp→real remap now run
  // live); Small UPS supplies its bespoke view (`SmallUpsEngineEditFormBody` →
  // `SmallUpsEditFormContent`) and its proven draft/name/clone semantics
  // (`smallUpsEngineBindings`) through the engine's per-section seams, so the
  // user-visible editor is unchanged. The create-draft submit flush
  // (`flushDraftSmallUpsConfigurations`) is untouched — the draft list stays
  // parent-owned in `formData.smallUpsConfigurations`.
  const draftSmallUps =
    (formData.smallUpsConfigurations as SmallUpsConfiguration[] | undefined) ??
    [];
  const draftInstances = useMemo(
    () =>
      draftSmallUps.map(
        unit => smallUpsToDescriptorInstance(unit) as DescriptorInstance,
      ),
    [draftSmallUps],
  );
  const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
  const handleDraftInstancesChange = useCallback(
    (instances: DescriptorInstance[]) =>
      updateFormData(
        'smallUpsConfigurations',
        instances.map(instance =>
          descriptorInstanceToSmallUps(
            instance,
            requestIdNum,
            SMALL_UPS_DESCRIPTOR,
          ),
        ),
      ),
    [updateFormData, requestIdNum],
  );

  // Engine handle → bespoke SmallUpsConfigurationsSaveHandle (CreateRequest's
  // footer-save/dismiss/scope-removal bridge keeps its existing contract).
  const registerInstanceSaveHandle = useMemo(() => {
    if (!registerSmallUpsSaveHandle) {
      return undefined;
    }
    return (handle: RepeatableInstanceSaveHandle | null) => {
      if (!handle) {
        registerSmallUpsSaveHandle(null);
        return;
      }
      const smallUpsHandle: SmallUpsConfigurationsSaveHandle = {
        isSmallUpsEditorOpen: handle.isInstanceEditorOpen,
        hasUnsavedSmallUpsEditor: handle.hasUnsavedInstanceEditor,
        commitOpenSmallUpsEditor: handle.commitOpenInstanceEditor,
        saveUnsavedSmallUpsIfNeeded: async () => {
          if (
            !handle.isInstanceEditorOpen() ||
            !handle.hasUnsavedInstanceEditor()
          ) {
            return true;
          }
          return handle.commitOpenInstanceEditor();
        },
        hasPendingSmallUpsChanges: () =>
          handle.hasPendingListChanges?.() ?? false,
        flushPendingSmallUpsChanges: async () => {
          await handle.flushPendingListChanges?.();
        },
        tryDismissSmallUpsEditor: handle.tryDismissInstanceEditor,
        discardOpenSmallUpsEditor: handle.discardOpenInstanceEditor,
        // Draft list is parent-owned (formData.smallUpsConfigurations) — scope
        // removal strips it there; the in-memory session reset is the
        // persisted-path (Phase 4b) concern.
        resetSessionAfterScopeRemoval: () => {},
      };
      registerSmallUpsSaveHandle(smallUpsHandle);
    };
  }, [registerSmallUpsSaveHandle]);

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <SmallUpsEngineEditFormBody {...props} />
    ),
    [],
  );

  return (
    <StepShell
      title="Small UPS"
      testID="create-form-header-small-ups"
      
    >
      <DescriptorEngineSection
        descriptor={SMALL_UPS_DESCRIPTOR}
        requestId={requestId}
        isEditing={!disabled}
        disabled={disabled}
        requestStage={stage}
        draftInstances={draftInstances}
        onDraftInstancesChange={handleDraftInstancesChange}
        legacyRequestFields={formData}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={offlineMode}
        editBody={editBody}
        createDraft={smallUpsCreateDraft}
        ensureNameForCreate={smallUpsEnsureNameForCreate}
        cloneForCreate={smallUpsCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onSmallUpsEditorActiveChange}
        testID="small-ups-configurations"
      />
    </StepShell>
  );
};

const SmallUpsPersistedLayout: React.FC<SmallUpsPersistedProps> = ({
  request,
  isEditing,
  editedData,
  showFullDetails = true,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits = {},
  registerInstanceSaveHandle,
  applyEditedDataPatch,
  smallUpsConfigurationsDirty = false,
  onSmallUpsEditorActiveChange,
}) => {
  const requestIdStr = String(request.id ?? '');
  const fieldDisabled = !isEditing;
  const stage = String(request.stage || request.sysRequestStage || 'DRAFT');
  const isOfflineRequest =
    request.id != null && String(request.id).startsWith('o-');

  const pdfFormData = useMemo(
    () => ({
      ...(request as unknown as Record<string, unknown>),
      ...editedData,
    }),
    [request, editedData],
  );

  // #997 Story 6.4 Phase 4b — the persisted layout runs on the descriptor
  // engine (mirror RackPower). Host-boundary display-list resolution mirrors the
  // bespoke subsection: while the user has Small UPS edits this session,
  // `editedData.smallUpsConfigurations` is authoritative (even when empty —
  // deleted-all must not resurrect); otherwise units query > request embed >
  // legacy flat-unit synthesis.
  const { data: queryUnits = [] } = useSmallUpsConfigurations(
    requestIdStr,
    true,
  );
  const editedUnits = editedData?.smallUpsConfigurations;
  const effectiveUnits = useMemo((): SmallUpsConfiguration[] => {
    if (
      isEditing &&
      smallUpsConfigurationsDirty &&
      Array.isArray(editedUnits)
    ) {
      return editedUnits as SmallUpsConfiguration[];
    }
    if (queryUnits.length > 0) {
      return queryUnits;
    }
    const embed = pdfFormData.smallUpsConfigurations;
    if (Array.isArray(embed) && embed.length > 0) {
      return embed as SmallUpsConfiguration[];
    }
    const legacyUnit = buildLegacySmallUpsFromRequestFields(
      pdfFormData,
      requestIdStr,
    );
    return legacyUnit ? [legacyUnit] : [];
  }, [
    isEditing,
    smallUpsConfigurationsDirty,
    editedUnits,
    queryUnits,
    pdfFormData,
    requestIdStr,
  ]);

  const legacyRequestFields = useMemo(
    () => ({ ...pdfFormData, smallUpsConfigurations: effectiveUnits }),
    [pdfFormData, effectiveUnits],
  );

  const editBody = useCallback(
    (props: RepeatableInstanceEditFormRenderProps) => (
      <SmallUpsEngineEditFormBody {...props} />
    ),
    [],
  );

  return (
    <View style={smallUpsPersistedStyles.container}>
      <PersistedStepHeader
        title="Small UPS"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={smallUpsPersistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              ) : (
                <EyeOff size={Platform.OS === 'web' ? 18 : 16} color="#666" />
              )}
              <Text style={smallUpsPersistedStyles.viewToggleText}>
                {isAdvancedView ? 'Advanced' : 'Basic'}
              </Text>
            </TouchableOpacity>
          ) : null
        }
      />

      <DescriptorEngineSection
        descriptor={SMALL_UPS_DESCRIPTOR}
        requestId={requestIdStr}
        isEditing={isEditing}
        // #997 QA-C: keep view-mode rows drillable; mutations stay gated by
        // isEditing/listReadOnly, so do NOT restore fieldDisabled (=!isEditing) here.
        disabled={false}
        requestStage={stage}
        showFullDetails={showFullDetails}
        legacyRequestFields={legacyRequestFields}
        applyEditedDataPatch={applyEditedDataPatch}
        attachmentsBySection={attachmentsBySection}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        offlineMode={isOfflineRequest || offlineMode}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        editBody={editBody}
        createDraft={smallUpsCreateDraft}
        ensureNameForCreate={smallUpsEnsureNameForCreate}
        cloneForCreate={smallUpsCloneForCreate}
        registerInstanceSaveHandle={registerInstanceSaveHandle}
        onInstanceEditorActiveChange={onSmallUpsEditorActiveChange}
        testID="small-ups-configurations"
      />
    </View>
  );
};

const smallUpsPersistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 8,
    paddingVertical: Platform.OS === 'web' ? 6 : 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 11,
    fontWeight: '500',
    color: '#666',
  },
});

export const SmallUps: React.FC<SmallUpsProps> = props =>
  isSmallUpsPersistedProps(props) ? (
    <SmallUpsPersistedLayout {...props} />
  ) : (
    <SmallUpsInitialLayout {...props} />
  );

SmallUps.displayName = 'SmallUps';

/** @deprecated Test / caption suites — compose path only */
export const SmallUpsStep = SmallUpsInitialLayout;
/** @deprecated Test / caption suites — persisted request detail branch */
export const SmallUpsInRequest = SmallUpsPersistedLayout;
